import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {BuilderV2Service} from "../../../../../../builder-v2.service";
import {AwsUploadService} from "../../../../../../../shared/services/aws-upload/aws-upload.service";
import {IAwsKeys} from "../../../../../../../shared/services/aws-upload/aws-keys.interface";
import {FigmaLinkComponent} from "../figma-link/figma-link.component";
import {BuilderSectionsInteractionService} from "../../../../../../builder-sections-interaction.service";
import {NotifierComponent} from "../../../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../../../shared/components/notifier/notification-type";

interface UploadedImage {
  src: string;
  name: string;
  dimensions: { width: number; height: number };
  id: string | null;
  isUploading: boolean;
}

@Component({
  selector: 'db-vc-image-upload',
  imports: [
    FigmaLinkComponent
  ],
  templateUrl: './vc-image-upload.component.html',
  styleUrl: './vc-image-upload.component.scss'
})
export class VcImageUploadComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Input() descInput!: ElementRef<HTMLTextAreaElement>;
  @Input() builderType: 'wvc' | 'section_based';
  uploadedImages: WritableSignal<UploadedImage[]> = signal<UploadedImage[]>([]);
  hasSrc = false;
  awsKeys: IAwsKeys | null = null;
  @Input() showFigmaLink = false;
  @Output() closeFigma = new EventEmitter<void>();
  figmaResp: any;

  // limit & size constants
  readonly MAX_IMAGES = 5;
  readonly MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
  @Output() fileInputTrigger = new EventEmitter<ElementRef<HTMLInputElement>>();

  constructor(private _builderV2Service: BuilderV2Service,
              private _uploadService: AwsUploadService,
              private _sectionInteractionsService: BuilderSectionsInteractionService) {
    effect(() => {
      this._sectionInteractionsService.vibeCodingImages = this.uploadedImages()
          .filter(img => !img.isUploading)
          .map(img => {
            return {
              url: img.src
            };
          });

      const isLoading = this.uploadedImages().some(img => img.isUploading);
      console.log(JSON.stringify(this.uploadedImages()));
      this._sectionInteractionsService.vibeCodingLoading.next(isLoading);
    });
  }

  ngOnInit() {}

  ngAfterViewInit() {
    this.fileInputTrigger.emit(this.fileInput);

    if (this.fileInput && this.fileInput.nativeElement) {
      document.addEventListener('paste',  this.handlePaste.bind(this));
    }
  }

  setFigmaResp(value: any) {
    this.figmaResp = value;
  }

  handleFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (this.uploadedImages().length >= 5) {
      NotifierComponent.notify(new Notification("File uploads are limited to 5 at a time.", NotificationType.DANGER));
      // Reset input value to allow re-selecting the same file
      input.value = '';
      return;
    }

    if (files && files.length) {
      this._builderV2Service.getAWS().subscribe({
        next: (resp: any) => {
          this.awsKeys = {
            bucket: 'wpvc-screenshots',
            file_path: '',
            sts_credentials: {
              AccessKeyId: resp.access_key_id,
              Expiration: resp.expiration,
              SecretAccessKey: resp.secret_access_key,
              SessionToken: resp.session_token,
            }
          };
          // process the FileList as an array
          this.processImageFiles(Array.from(files));
          // Reset input value to allow re-selecting the same file
          input.value = '';
        },
        error: (err) => {
          console.error('Failed to get AWS keys for file change', err);
          // Reset input value even on error
          input.value = '';
        }
      });
    }
  }

  openFileDialog(): void {
    this.fileInput.nativeElement.click();
  }

  /**
   * Handle paste events on the input - extract images from clipboard and process them.
   */
  async handlePaste(event: ClipboardEvent): Promise<void> {
    // Disable paste for section_based (classic) builder
    if (this.builderType === 'section_based') return;

    if (!event.clipboardData) return;

    // Collect image files from clipboard items
    const items = Array.from(event.clipboardData.items || []);
    const imageFiles: File[] = [];

    for (const item of items) {
      // If item is a file and its type starts with image/
      if (item.kind === 'file' && item.type && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      } else if (item.kind === 'string') {
        // Some browsers sometimes provide a base64 URI via "text/html" or "text/plain"
        // Attempt to extract a dataURL if present
        if (item.type === 'text/html' || item.type === 'text/plain') {
          try {
            const str = await new Promise<string | null>((resolve) =>
                item.getAsString(s => resolve(s || null))
            );
            if (str) {
              // attempt to find a data URL (data:image/...)
              const dataUrlMatch = str.match(/data:image\/[a-zA-Z]+;base64,[^\s'"]+/);
              if (dataUrlMatch) {
                const dataUrl = dataUrlMatch[0];
                const fileFromDataUrl = this.dataUrlToFile(dataUrl, `pasted-${Date.now()}.png`);
                if (fileFromDataUrl) imageFiles.push(fileFromDataUrl);
              }
            }
          } catch (err) {
            // ignore
          }
        }
      }
    }

    if (imageFiles.length === 0) {
      return;
    }

    // Ensure we have AWS keys before processing paste
    this._builderV2Service.getAWS().subscribe({
      next: (resp: any) => {
        this.awsKeys = {
          bucket: 'wpvc-screenshots',
          file_path: '',
          sts_credentials: {
            AccessKeyId: resp.access_key_id,
            Expiration: resp.expiration,
            SecretAccessKey: resp.secret_access_key,
            SessionToken: resp.session_token,
          }
        };
        // process the FileList as an array
        const availableSlots = this.MAX_IMAGES - this.uploadedImages().length;
        if (availableSlots <= 0) return;
        console.log(imageFiles, imageFiles.slice(0, availableSlots), availableSlots);
        this.processImageFiles(imageFiles.slice(0, availableSlots));
      },
      error: (err) => {
        console.error('Failed to get AWS keys for file change', err);
      }
    });
    // Process only up to available slots
  }

  /**
   * Shared logic for processing an array of File objects (used by file input change and paste)
   */
  async processImageFiles(files: File[]): Promise<void> {
    if (!files || files.length === 0) return;

    const imageFiles = files.filter(f => f.type && f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    const availableSlots = this.MAX_IMAGES - this.uploadedImages().length;
    if (availableSlots <= 0) return;
    const imagesToProcess = imageFiles.slice(0, availableSlots);
    const startIndex = this.uploadedImages().length;

    for (const [i, file] of imagesToProcess.entries()) {
      // File size check
      if (file.size > this.MAX_SIZE_BYTES) {
        console.log('File size exceeded:', file.size, 'bytes for file:', file.name);
        NotifierComponent.notify(new Notification("The file size is over the 5 MB limit.", NotificationType.DANGER));
        continue;
      }

      console.log('File size OK:', file.size, 'bytes for file:', file.name);

      const fileName = file.name || `pasted-${Date.now()}.png`;
      const tempImageIndex = startIndex + i;

      // Add temporary placeholder (loading)
      const tempImage: UploadedImage = {
        src: '',
        name: fileName,
        dimensions: {width: 0, height: 0},
        id: null,
        isUploading: true,
      };

      // append placeholder
      this.uploadedImages.update(arr => [...arr, tempImage]);
      console.log(tempImage);
      this.hasSrc = true;

      // Read file as data URL
      const reader = new FileReader();
      reader.onload = () => {
        const imageSrc = reader.result as string;

        // create Image to get dimensions
        const imgEl = new Image();
        imgEl.onload = async () => {
          const dimensions = {width: imgEl.width, height: imgEl.height};

          // Update the temporary image with src & dims (still uploading)
          this.uploadedImages.update(arr =>
              arr.map((u, idx) => {
                if (
                    idx === tempImageIndex &&
                    u.name === fileName &&
                    u.isUploading &&
                    u.src === ''
                ) {
                  return {...u, src: imageSrc, dimensions, isUploading: true};
                }
                return u;
              })
          );

          // Upload the image if awsKeys available
          try {
            const id = new Date().getTime().toString();
            let uploadResult: string | null = null;
            if (this.awsKeys) {
              uploadResult = await this._uploadService.simpleFileUpload(this.awsKeys, file, 'us-east-1');
            } else {
              console.warn('No awsKeys available; skipping remote upload and using local data URL.');
            }
            console.log(uploadResult);
            // update final uploaded item with returned url/id and mark not uploading
            this.uploadedImages.update(arr =>
                arr.map(u => {
                  if (u.name === fileName && u.isUploading) {
                    return {
                      ...u,
                      src: uploadResult || u.src,
                      id: id,
                      isUploading: false,
                    };
                  }
                  return u;
                })
            );
            console.log(this.uploadedImages());

          } catch (err) {
            console.error('Error uploading image:', err);
            // Keep local image but mark as not uploading
            this.uploadedImages.update(arr =>
                arr.map(u => {
                  if (u.name === fileName && u.isUploading) {
                    return {...u, src: imageSrc, isUploading: false};
                  }
                  return u;
                })
            );
          }
        };

        imgEl.onerror = () => {
          // If image can't be loaded to measure, still set src and mark not uploading
          this.uploadedImages.update(arr =>
              arr.map((u, idx) => {
                if (
                    idx === tempImageIndex &&
                    u.name === fileName &&
                    u.isUploading &&
                    u.src === ''
                ) {
                  return {...u, src: imageSrc, dimensions: {width: 0, height: 0}, isUploading: false};
                }
                return u;
              })
          );
        };

        // start loading to trigger onload/onerror
        imgEl.src = imageSrc;
      };

      reader.readAsDataURL(file);
    }
  }

  /**
   * Utility: convert a data URL to a File object
   */
  dataUrlToFile(dataUrl: string, filename: string): File | null {
    try {
      const arr = dataUrl.split(',');
      const mimeMatch = arr[0].match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : 'image/png';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    } catch (err) {
      console.error('Failed to convert dataUrl to file', err);
      return null;
    }
  }

  removeImageByIndex(index: number): void {
    this.uploadedImages.update(images =>
        images.filter((_, i) => i !== index)
    );

    if (this.uploadedImages().length === 0) {
      this.hasSrc = false;
      // Reset images data in the service when all images are removed
      this._sectionInteractionsService.vibeCodingImages = [];
    }
  }


  onCloseFigma(): void {
    // Reset figmaResp to null when figma link is closed
    this.figmaResp = null;
    this.closeFigma.emit();
  }

  ngOnDestroy() {
    document.removeEventListener('paste', this.handlePaste);
  }
}
